import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { Building2, Check, ChevronDown, MapPin, Shield, X } from "lucide-react";
import { showSuccess, showError, showConfirm } from "../utils/notifications";
import ActionDropdown from "./ActionDropdown";
import { AtlasListToolbar, TableHeaderCell } from "./list-toolbar";
import { EmbeddedFilterPanel, useEmbeddedFilterLayout } from "./embedded-filter";
import {
  AtlasFilterChipBar,
  FilterSelect,
} from "./filters";

function buildContactClientsFilterChips({ roleFilter }) {
  if (!roleFilter) return [];
  return [
    {
      id: "role",
      key: "roleFilter",
      fieldId: "contact-clients-role",
      label: `Role: ${roleFilter}`,
    },
  ];
}

export default function ContactClientsTable({ accountId }) {
  const clientsFilterPanel = useEmbeddedFilterLayout();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [pageSize, setPageSize] = useState(100);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);

  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientQuery, setSelectedClientQuery] = useState("");
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [loadingModalData, setLoadingModalData] = useState(false);
  const [actionBusyKey, setActionBusyKey] = useState("");
  const roleDropdownRef = useRef(null);

  const getDefaultLocationId = (locationRows = []) => {
    const primaryLoc = locationRows.find((loc) => {
      const p = loc?.primary;
      return p === 1 || p === true || String(p) === "1";
    });
    const target = primaryLoc || locationRows[0];
    return target ? String(target.location_id || target.client_location_id || "") : "";
  };

  const clientSuggestions = (query) => {
    const q = String(query || "").toLowerCase().trim();
    if (q === "") return (clients || []).slice(0, 50);
    return (clients || [])
      .filter((c) => (c.name || "").toLowerCase().includes(q))
      .slice(0, 50);
  };

  const fetchClients = async () => {
    if (!accountId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BASE_URL}/api/personnel/accounts/${accountId}/client-mappings`);
      const data = res.data?.data ? res.data.data : [];

      const grouped = {};
      data.forEach((item) => {
        const key = `${item.clientId}-${item.clientLocationId}`;
        if (!grouped[key]) {
          grouped[key] = {
            ...item,
            roles: [],
            objectId: item.clientLocationId || item.clientId,
            disabled: Boolean(item.disabled),
          };
        }
        if (!item.disabled) {
          grouped[key].disabled = false;
        }
        if (item.roleName && !grouped[key].roles.includes(item.roleName)) {
          grouped[key].roles.push(item.roleName);
        }
      });
      setRows(Object.values(grouped));
    } catch (err) {
      console.error("Failed to load clients for account", accountId, err);
      setError(err?.response?.data?.message || err.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [accountId]);

  const loadModalData = async () => {
    setLoadingModalData(true);
    try {
      const [clientsRes, rolesRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/clients/clients?limit=1000`),
        axios.get(`${BASE_URL}/api/personnel/roles`, { params: { level: "client" } }),
      ]);
      const fetchedRoles = rolesRes.data?.data || [];
      setClients(clientsRes.data?.data || []);
      setRoles(fetchedRoles);

      if (selectedMapping) {
        const ids = [];
        selectedMapping.roles.forEach((name) => {
          const r = fetchedRoles.find((fr) => fr.name === name);
          if (r) ids.push(r.id ?? r.role_id);
        });
        setSelectedRoleIds(ids);
      }
    } catch (err) {
      console.error("Failed to load modal data", err);
    } finally {
      setLoadingModalData(false);
    }
  };

  useEffect(() => {
    if (isAssignModalOpen) {
      loadModalData();
    }
  }, [isAssignModalOpen]);

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setSelectedMapping(null);
    setSelectedClientId("");
    setSelectedClientQuery("");
    setShowClientSuggestions(false);
    setSelectedLocationId("");
    setSelectedRoleIds([]);
    setLocations([]);
  };

  const handleEditMapping = (mapping) => {
    setSelectedMapping(mapping);
    setSelectedClientId(mapping.clientId);
    setSelectedLocationId(mapping.clientLocationId || "");
    setIsAssignModalOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedClientId && !selectedMapping) {
      (async () => {
        try {
          const res = await axios.get(`${BASE_URL}/api/clients/${selectedClientId}/locations`);
          const nextLocations = res.data?.data || [];
          setLocations(nextLocations);
          setSelectedLocationId(getDefaultLocationId(nextLocations));
        } catch (err) {
          console.error("Failed to load locations", err);
          setLocations([]);
          setSelectedLocationId("");
        }
      })();
    }
  }, [selectedClientId, selectedMapping]);

  const toggleRole = (roleId) => {
    const rid = Number.isFinite(Number(roleId)) ? Number(roleId) : String(roleId);
    setSelectedRoleIds((prev) =>
      prev.includes(rid) ? prev.filter((id) => id !== rid) : [...prev, rid],
    );
  };

  const handleAssign = async () => {
    if (!selectedClientId || selectedRoleIds.length === 0) {
      return showError("Client and at least one Role are required");
    }
    setAssigning(true);
    try {
      const targetId = selectedLocationId || selectedClientId;
      const payload = {
        objectId: targetId,
        roleIds: selectedRoleIds,
      };

      if (selectedMapping) {
        await axios.put(`${BASE_URL}/api/personnel/accounts/${accountId}/client-mappings`, payload);
        showSuccess("Assignment updated");
      } else {
        await axios.post(`${BASE_URL}/api/personnel/accounts/${accountId}/client-mappings`, payload);
        showSuccess("Assignment(s) saved");
      }

      closeAssignModal();
      fetchClients();
    } catch (err) {
      console.error("Assignment failed", err);
      showError(err.response?.data?.message || "Failed to save assignment");
    } finally {
      setAssigning(false);
    }
  };

  const filteredRows = rows.filter(
    (r) =>
      (r.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.locationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.roles?.some((role) => role.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (!roleFilter ||
        (r.roles || []).some(
          (role) => String(role).toLowerCase() === roleFilter.toLowerCase(),
        )),
  );

  const visibleRows = filteredRows.slice(0, pageSize);

  const roleOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.flatMap((r) => (Array.isArray(r.roles) ? r.roles : [])).filter(Boolean)),
      )
        .sort((a, b) => String(a).localeCompare(String(b)))
        .map((role) => ({ value: role, label: role })),
    [rows],
  );

  const filterChips = useMemo(
    () => buildContactClientsFilterChips({ roleFilter }),
    [roleFilter],
  );

  const clearFilters = () => {
    setRoleFilter("");
    setSearchTerm("");
  };

  const rowKeyOf = (r) => `${r.clientId}-${r.clientLocationId || "client"}`;

  const handleSetInactive = async (mapping) => {
    const rowKey = rowKeyOf(mapping);
    setActionBusyKey(rowKey);
    try {
      await axios.put(`${BASE_URL}/api/personnel/accounts/${accountId}/client-mappings/state`, {
        objectId: mapping.objectId,
        disabled: true,
      });
      showSuccess("Mapping set as inactive");
      await fetchClients();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to set mapping inactive");
    } finally {
      setActionBusyKey("");
    }
  };

  const handleSetActive = async (mapping) => {
    const rowKey = rowKeyOf(mapping);
    setActionBusyKey(rowKey);
    try {
      await axios.put(`${BASE_URL}/api/personnel/accounts/${accountId}/client-mappings/state`, {
        objectId: mapping.objectId,
        disabled: false,
      });
      showSuccess("Mapping set as active");
      await fetchClients();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to set mapping active");
    } finally {
      setActionBusyKey("");
    }
  };

  const handleDeleteMapping = async (mapping) => {
    const confirmed = await showConfirm("Delete this client mapping?");
    if (!confirmed) return;
    const rowKey = rowKeyOf(mapping);
    setActionBusyKey(rowKey);
    try {
      await axios.delete(`${BASE_URL}/api/personnel/accounts/${accountId}/client-mappings`, {
        params: { objectId: mapping.objectId },
      });
      showSuccess("Mapping deleted");
      await fetchClients();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to delete mapping");
    } finally {
      setActionBusyKey("");
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="mb-4 flex items-center gap-2 text-xl font-semibold text-gray-900">
        <Building2 className="h-5 w-5 text-sidebar-active" />
        Client Mappings
      </h3>

      <AtlasListToolbar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search clients or roles..."
        onFilterClick={clientsFilterPanel.toggle}
        filterOpen={clientsFilterPanel.isOpen}
        onRefresh={fetchClients}
        refreshDisabled={loading}
        refreshSpinning={loading}
        resultsMeta={{
          startRow: filteredRows.length === 0 ? 0 : 1,
          endRow: Math.min(pageSize, filteredRows.length),
          totalCount: filteredRows.length,
          pageSize,
          pageSizeOptions: [10, 25, 50, 100],
          onPageSizeChange: setPageSize,
          showSort: false,
        }}
        actions={
          <button
            type="button"
            onClick={() => setIsAssignModalOpen(true)}
            className="whitespace-nowrap rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
          >
            Assign Client
          </button>
        }
        chipBar={
          filterChips.length > 0 ? (
            <AtlasFilterChipBar
              chips={filterChips}
              onChipClick={(chip) => clientsFilterPanel.focusField(chip.fieldId)}
              onRemove={() => setRoleFilter("")}
              onClearAll={clearFilters}
            />
          ) : null
        }
      />

      <EmbeddedFilterPanel
        isOpen={clientsFilterPanel.isOpen}
        onClose={clientsFilterPanel.close}
        onClear={clearFilters}
        footerMode="clearOnly"
      >
        <FilterSelect
          id="contact-clients-role"
          label="Role"
          labelVariant="embedded"
          value={roleFilter}
          onChange={setRoleFilter}
          options={[{ value: "", label: "All Roles" }, ...roleOptions]}
          placeholder={null}
        />
      </EmbeddedFilterPanel>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 rounded-lg border border-border bg-card">
          <div className="mb-2 h-8 w-8 animate-spin rounded-full border-b-2 border-sidebar-active" />
          <div className="text-sm font-medium text-muted-foreground">Loading mapped clients...</div>
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
          <X className="h-4 w-4" /> {error}
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card py-12 text-center">
          <Building2 className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <div className="text-sm font-medium text-muted-foreground">
            {searchTerm || roleFilter
              ? "No matches found for your search or filters"
              : "No mapped clients for this contact"}
          </div>
          {!searchTerm && !roleFilter && (
            <button
              type="button"
              onClick={() => setIsAssignModalOpen(true)}
              className="mt-4 text-sm font-semibold text-sidebar-active hover:underline"
            >
              Assign to a client now
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card no-mobile-table-stack">
          <table className="w-full min-w-[500px] table-fixed text-sm">
            <thead className="bg-gray-50">
              <tr>
                <TableHeaderCell label="Client" className="w-[38%]" />
                <TableHeaderCell label="Location" className="w-[14%]" />
                <TableHeaderCell label="Role" className="w-[28%] text-center" />
                <TableHeaderCell label="Actions" className="w-[20%] text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visibleRows.map((r, idx) => (
                <tr
                  key={`${r.clientId}-${r.clientLocationId}-${idx}`}
                  className="transition-colors hover:bg-table-row-hover"
                >
                  <td className="px-4 py-3 font-medium">
                    <button
                      type="button"
                      onClick={() => handleEditMapping(r)}
                      className="break-words text-left text-sm font-medium text-sidebar-active hover:underline"
                    >
                      {r.clientName}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {r.locationName || "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {(r.roles || []).map((role, ridx) => (
                      <span
                        key={ridx}
                        className="mr-1 inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-foreground"
                      >
                        {role}
                      </span>
                    ))}
                    {(!r.roles || r.roles.length === 0) && (
                      <span className="text-muted-foreground">-</span>
                    )}
                    {r.disabled && (
                      <span className="ml-1 inline-flex items-center rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <ActionDropdown
                      disabled={actionBusyKey === rowKeyOf(r)}
                      actions={[
                        r.disabled
                          ? {
                              label: "Set as active",
                              onClick: () => handleSetActive(r),
                              className: "text-amber-600",
                            }
                          : {
                              label: "Set as inactive",
                              onClick: () => handleSetInactive(r),
                              className: "text-amber-600",
                            },
                        {
                          label: "Delete",
                          onClick: () => handleDeleteMapping(r),
                          className: "text-red-600",
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAssignModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
              <h3 className="text-lg font-bold text-gray-900">Assign Contact to Client</h3>
              <button
                type="button"
                onClick={closeAssignModal}
                className="p-1 text-gray-400 transition-colors hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-5 overflow-y-auto p-6 pb-48">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">
                  Client
                </label>
                {selectedMapping ? (
                  <div className="py-1 text-base font-semibold text-gray-800">
                    {selectedMapping.clientName}
                  </div>
                ) : (
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Type to search or pick a client"
                      value={selectedClientQuery}
                      onChange={(e) => {
                        setSelectedClientQuery(e.target.value);
                        setSelectedClientId("");
                        setShowClientSuggestions(true);
                        setSelectedLocationId("");
                      }}
                      onFocus={() => setShowClientSuggestions(true)}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm font-medium text-gray-700 focus:border-sidebar-active focus:outline-none focus:ring-2 focus:ring-sidebar-active/20"
                    />
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    {showClientSuggestions && clientSuggestions(selectedClientQuery).length > 0 && (
                      <ul className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-auto rounded-xl border border-gray-200 bg-white shadow-xl">
                        {clientSuggestions(selectedClientQuery).map((c) => (
                          <li
                            key={c.id}
                            onMouseDown={() => {
                              setSelectedClientId(String(c.id));
                              setSelectedClientQuery(c.name);
                              setShowClientSuggestions(false);
                              setSelectedLocationId("");
                            }}
                            className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-50"
                          >
                            {c.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">
                  Location (optional)
                </label>
                {selectedMapping ? (
                  <div className="py-1 text-base font-semibold text-gray-800">
                    {selectedMapping.locationName || "Default"}
                  </div>
                ) : (
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <select
                      value={selectedLocationId}
                      onChange={(e) => setSelectedLocationId(e.target.value)}
                      disabled={!selectedClientId}
                      className="w-full appearance-none rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm font-medium text-gray-700 focus:border-sidebar-active focus:outline-none focus:ring-2 focus:ring-sidebar-active/20 disabled:opacity-50"
                    >
                      <option value="">Select a Location (optional)</option>
                      {locations.map((l) => (
                        <option key={l.location_id} value={l.location_id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                )}
              </div>

              <div className="space-y-1.5" ref={roleDropdownRef}>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">
                  Role <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center gap-2 border-b border-gray-200 pb-2">
                  <div className="flex min-h-[40px] flex-1 flex-wrap items-center gap-2">
                    {selectedRoleIds.map((rid) => {
                      const roleObj = roles.find((r) => String(r.role_id ?? r.id) === String(rid));
                      return (
                        <span
                          key={rid}
                          className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-1.5 text-sm font-semibold text-gray-700"
                        >
                          {roleObj?.name || rid}
                          <X
                            className="h-4 w-4 cursor-pointer text-gray-400 hover:text-gray-600"
                            onClick={() => toggleRole(rid)}
                          />
                        </span>
                      );
                    })}
                    {selectedRoleIds.length === 0 && (
                      <span className="text-sm italic text-gray-400">No roles selected</span>
                    )}
                  </div>
                  <X className="h-5 w-5 cursor-pointer text-gray-900" onClick={() => setSelectedRoleIds([])} />
                </div>
                <div className="relative mt-2">
                  <Shield className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search and add roles..."
                    value={roleSearchTerm}
                    onFocus={() => setIsRoleDropdownOpen(true)}
                    onChange={(e) => {
                      setRoleSearchTerm(e.target.value);
                      setIsRoleDropdownOpen(true);
                    }}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm font-medium text-gray-700 focus:border-sidebar-active focus:outline-none focus:ring-2 focus:ring-sidebar-active/20"
                  />
                  {isRoleDropdownOpen && (
                    <div className="absolute left-0 right-0 z-[70] mt-2 max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
                      {roles
                        .filter((r) =>
                          (r.name || "").toLowerCase().includes(roleSearchTerm.toLowerCase()),
                        )
                        .map((r) => {
                          const rid = r.role_id ?? r.id;
                          const normalizedRid = Number.isFinite(Number(rid))
                            ? Number(rid)
                            : String(rid);
                          const isSelected = selectedRoleIds.includes(normalizedRid);
                          return (
                            <div
                              key={rid}
                              onClick={() => {
                                toggleRole(rid);
                                setRoleSearchTerm("");
                              }}
                              className={`flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                                isSelected
                                  ? "bg-red-50 font-bold text-red-700"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {r.name}
                              {isSelected && <Check className="h-4 w-4" />}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-start gap-8 border-t border-gray-50 bg-white px-6 py-5">
              <button
                type="button"
                onClick={handleAssign}
                disabled={assigning || (roles.length > 0 && selectedRoleIds.length === 0)}
                className={`text-sm font-bold uppercase tracking-wider transition-colors ${
                  assigning || (roles.length > 0 && selectedRoleIds.length === 0)
                    ? "cursor-not-allowed text-gray-300"
                    : "cursor-pointer text-sidebar-active hover:text-red-600"
                }`}
              >
                SAVE
              </button>
              <button
                type="button"
                onClick={closeAssignModal}
                className="text-sm font-bold uppercase tracking-wider text-gray-900 transition-colors hover:text-gray-600"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
